import { useMachine } from '@xstate/react';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { ProductType } from '@tink/core/src/types';
import { ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { AwaitingMobileBankIdAuthenticationTarget } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/molecules/AwaitingMobileBankIdAuthenticationTarget/AwaitingMobileBankIdAuthenticationTarget';
import { BankIdAuthWithSupplementalContainer } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/organisms/BankIdAuthWithSupplementalContainer/BankIdAuthWithSupplementalContainer';
import { createBankIdMachine } from '../../machines/bankIdMachine';
import { HandoffAwaitingScreenView } from '../../../components/screens/AwaitingCredentialsScreen/organisms/HandoffAwaitingScreen/HandoffAwaitingScreenView';
import { getBankIdAuthenticationType } from '../../../utilities/provider-utils';
import { AwaitingMobileBankIdAuthenticationLocalPromptView } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/molecules/AwaitingMobileBankIdAuthenticationLocalPrompt/AwaitingMobileBankIdAuthenticationLocalPromptView';
import { LoadingOverlay } from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { AuthenticationPromptType, CredentialsEntity } from '../../../models/credentials';
import { ProviderEntity } from '../../../models/provider';
import { BankIdRedirector } from '../../../utilities/bankid-redirector/bankid-redirector';
import { shouldRedirectToReferPage } from '../../../models/configuration';
import { PageView, UserInteractionEvent } from '../../../models/analytics';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { useCredentialsCopy } from '../../../components/screens/SubmitCredentials/hooks/useCredentialsCopy';

export type BankIdMachineViewProps = {
  isPaymentSca: boolean;
  footerDisclaimer: React.ReactNode | undefined;
  onExecute: VoidFunction | undefined;
  credentials: CredentialsEntity;
  provider: ProviderEntity;
  productType: ProductType;
  configurationData: ConfigurationModel;
  forcedPromptType?: AuthenticationPromptType;
  onDone: (c: CredentialsEntity) => void;
  onError: (c: CredentialsEntity) => void;
  onBack: () => void;
  onOpenBankId?: (credentialsId: string) => void;
};

export const BankIdMachineView = ({
  isPaymentSca,
  footerDisclaimer,
  onExecute,
  credentials,
  provider,
  productType,
  configurationData,
  forcedPromptType,
  onDone,
  onError,
  onBack,
  onOpenBankId,
}: BankIdMachineViewProps) => {
  const { heading } = useCredentialsCopy();
  const machine = useMemo(
    () =>
      createBankIdMachine({
        initialContext: {
          selectedProvider: provider,
          credentials,
          forcedPromptType,
        },
      }),
    [provider, credentials, forcedPromptType]
  );
  const [current, send] = useMachine(machine);
  const { tinklinkAnalytics } = useAnalytics();

  const openBankId = useCallback(() => {
    const redirector = new BankIdRedirector({
      credentials: current.context.credentials,
      productType,
      redirectToReferPage: shouldRedirectToReferPage(configurationData),
    });
    redirector.open();
    onExecute?.();

    if (onOpenBankId) onOpenBankId(current.context.credentials.id);
  }, [configurationData, current.context.credentials, onExecute, onOpenBankId, productType]);

  useEffect(() => {
    if (current.matches('uiLocalAutoRedirect')) {
      send('EVENT_AUTO_REDIRECT');

      tinklinkAnalytics.sendInteractionEvent({
        action: `${PageView.BANKID_LOCAL_AWAITING_SCREEN}/${UserInteractionEvent.OPEN_BANKID}`,
        view: PageView.BANKID_LOCAL_AWAITING_SCREEN,
      });
      openBankId();
    }
  }, [current, forcedPromptType, openBankId, send, tinklinkAnalytics]);

  useEffect(() => {
    if (current.matches('uiDone')) onDone(current.context.credentials);

    if (current.matches('uiError')) {
      onError(current.context.credentials);

      if (current.context.error) throw current.context.error;
    }

    if (current.matches('uiBack')) onBack();
  }, [credentials, configurationData, productType, current, onBack, onDone, onError, onOpenBankId]);

  if (current.matches('uiSelectTarget')) {
    return (
      <AwaitingMobileBankIdAuthenticationTarget
        pageView={PageView.BANKID_TARGET_AWAITING_SCREEN}
        provider={current.context.selectedProvider}
        onLocalDeviceClick={() => {
          if (userAgent.isMobile) {
            tinklinkAnalytics.sendInteractionEvent({
              action: `${PageView.BANKID_TARGET_AWAITING_SCREEN}/${UserInteractionEvent.OPEN_BANKID}`,
              view: PageView.BANKID_TARGET_AWAITING_SCREEN,
            });
            openBankId();
          }
          send('EVENT_USER_CLICK_SELECT_LOCAL');
        }}
        onRemoteDeviceClick={() => {
          send('EVENT_USER_CLICK_SELECT_REMOTE');
          onExecute?.();
        }}
        onBack={onBack}
        footerDisclaimer={footerDisclaimer}
        heading={heading}
      />
    );
  }

  if (
    current.matches('uiPollingLocal') ||
    current.matches('uiLocalAutoRedirect') ||
    current.matches('uiPollingRemoteWithoutToken')
  )
    return (
      <AwaitingMobileBankIdAuthenticationLocalPromptView
        isPay={isPaymentSca}
        onOpenBankId={openBankId}
        onBack={onBack}
      />
    );

  if (current.matches('uiPollingRemoteWithToken'))
    return (
      <BankIdAuthWithSupplementalContainer
        onBack={onBack}
        heading={heading}
        provider={current.context.selectedProvider}
        supplementalInformation={{
          data: current.context.credentials.supplementalInformation!,
          type: getBankIdAuthenticationType(provider.name),
        }}
        isPay={isPaymentSca}
        footerDisclaimer={footerDisclaimer}
      />
    );

  if (current.matches('uiPollingRemoteWithTokenHandoff')) {
    return (
      <Fragment key={current.context.credentials.statusUpdated}>
        <HandoffAwaitingScreenView
          heading={heading}
          allowExit={!isPaymentSca}
          credentials={current.context.credentials}
          onPollerError={() => send('EVENT_POLLER_ERROR')}
        />
      </Fragment>
    );
  }

  return <LoadingOverlay />;
};
